import { quoteApi } from "@api";
import { normalizeComment, quoteReferenceMatches } from "src/Utilities/customerQuoteHelpers";
import { ICustomerQuote, IPayNowResponseData } from "@interfaces";

/**
 * Async orchestration for the public customer-quote flow. Kept separate from
 * the page component so the API interactions can be unit-tested with a mocked
 * `@api`, and out of `customerQuoteHelpers.ts` so those stay axios-free.
 */

/**
 * Load a quote via the public link. Unwraps the `IAPIResult` envelope and
 * treats a missing/empty body as a 404 so the page shows the not-found state
 * instead of rendering an empty quote.
 */
export const fetchCustomerQuote = async (
    quoteId: string,
    quoteToken: string,
    randomString: string,
): Promise<ICustomerQuote> => {
    const res = await quoteApi.getCustomerQuote(quoteId, quoteToken, randomString);
    const body = res?.data as { data?: ICustomerQuote } | ICustomerQuote | undefined;
    const quote = (body as { data?: ICustomerQuote })?.data ?? (body as ICustomerQuote);
    if (!quote || quote.ordersId === undefined || quote.ordersId === null) {
        const notFound = new Error("Quote not found") as Error & {
            response?: { status: number };
        };
        notFound.response = { status: 404 };
        throw notFound;
    }
    // Verify the booking reference embedded in the link token (the part to the
    // right of the `::` in the decoded base64) matches the quote the backend
    // returned. A mismatch means a wrong/tampered link → treat as invalid.
    if (!quoteReferenceMatches(quoteToken, quote)) {
        const invalid = new Error("Quote reference mismatch") as Error & {
            response?: { status: number };
        };
        invalid.response = { status: 403 };
        throw invalid;
    }
    return quote;
};

/** Persist the customer's comment back onto the quote (trimmed). */
export const persistCustomerComment = (
    quoteId: string,
    quoteToken: string,
    randomString: string,
    comment: string,
) =>
    quoteApi.saveCustomerQuoteComment(
        quoteId,
        quoteToken,
        randomString,
        normalizeComment(comment),
    );

/**
 * Outcome of asking the backend to pay an existing quote. The same endpoint
 * either hands back a hosted-payment URL (balance owing) or reports that no
 * payment is required (the booking is already settled in TDMS), so the page can
 * branch instead of treating the no-payment case as a failure.
 */
export type CustomerQuotePaymentResult =
    | { kind: "redirect"; redirectUrl: string }
    | { kind: "no_payment_required"; bookingReference?: string }
    | { kind: "balance_mismatch"; owingBalance: number; bookingReference?: string };

/**
 * Pay an existing quote directly. The quote already holds every booking detail
 * (products, dates, fares, travellers, voucher), so the customer just settles
 * the owing balance — there is no cart and no availability re-check. The
 * backend validates the link and asks TDMS for a payment gateway URI on the
 * quote's existing booking reference. When something is owed it returns
 * `payNow.redirectUrl` (we sanitise the escaped slashes exactly like the
 * authenticated quote checkout in `QuotesDetail`); when the balance is already
 * ≤ 0 it returns `is_payment_required: false` and no gateway URL.
 */
export const startCustomerQuotePayment = async (
    quoteId: string,
    quoteToken: string,
    randomString: string,
    owingBalance = 0,
): Promise<CustomerQuotePaymentResult> => {
    const res = await quoteApi.createCustomerQuoteOrder(quoteId, quoteToken, randomString);
    const body = res?.data as
        | { data?: IPayNowResponseData & { is_payment_required?: boolean } }
        | undefined;
    const data = body?.data;
    const redirectUrl = data?.payNow?.redirectUrl;

    if (data?.is_payment_required === false) {
        // The backend says nothing is owed, but the quote still shows an
        // outstanding balance — these contradict each other (a TDMS/backend
        // sync issue). Never tell the customer they're paid up when they aren't;
        // surface the discrepancy so the page can ask them to contact support.
        const owing = Number(owingBalance) || 0;
        if (owing > 0) {
            return {
                kind: "balance_mismatch",
                owingBalance: owing,
                bookingReference: data?.bookingReference,
            };
        }
        // Genuinely settled: balance is zero on both sides. Confirm rather than
        // sending the customer to an empty payment page.
        return { kind: "no_payment_required", bookingReference: data?.bookingReference };
    }

    // Payment is expected but the gateway returned no URL — a genuine failure.
    if (!redirectUrl) {
        throw new Error("No payment redirect URL returned");
    }
    return { kind: "redirect", redirectUrl: redirectUrl.replaceAll("\\", "") };
};
